import { useQuery } from '@tanstack/react-query';

import ledgerViewsApi from '../../../api/ledgerViewsApi';
import { ARWorkspaceView, LedgerViewMode } from '../../../api/types';
import { formatDateKey, startOfWeekMonday } from '../../../utils/tutorialDates';

interface UseARQueriesParams {
    ledgerViewMode: LedgerViewMode;
    arSource: ARWorkspaceView;
    selectedBusinessId?: string;
}

const useARQueries = ({
    ledgerViewMode,
    arSource,
    selectedBusinessId,
}: UseARQueriesParams) => {
    const isPureArView = ledgerViewMode === 'ar' && arSource !== 'ap_bills' && arSource !== 'ap_vendors' && arSource !== 'ap_ledger';

    const { data: arWeeklyMetrics = [] } = useQuery({
        queryKey: ['ar-weekly-metrics', selectedBusinessId],
        queryFn: () => ledgerViewsApi.getArWeeklyMetrics(selectedBusinessId || undefined),
        enabled: isPureArView && !!selectedBusinessId,
    });

    const currentWeekStart = formatDateKey(startOfWeekMonday(new Date()));
    const latestArWeeklyMetric = arWeeklyMetrics.find((metric) => String(metric.week_start || '').slice(0, 10) === currentWeekStart) || null;

    const { data: arWeeklyActionsData } = useQuery({
        queryKey: ['ar-weekly-actions', selectedBusinessId, currentWeekStart],
        queryFn: () => ledgerViewsApi.getArWeeklyActions({ business_id: selectedBusinessId || undefined, week_start: currentWeekStart, page: 1, page_size: 5 }),
        enabled: isPureArView && !!selectedBusinessId,
    });

    const { data: arWeeklyRevenueContributionData } = useQuery({
        queryKey: ['ar-weekly-revenue-contributions', selectedBusinessId, currentWeekStart],
        queryFn: () => ledgerViewsApi.getArWeeklyRevenueContributions({
            business_id: selectedBusinessId || undefined,
            week_start: currentWeekStart,
            page: 1,
            page_size: 12,
        }),
        enabled: isPureArView && !!selectedBusinessId,
    });

    return {
        arWeeklyMetrics,
        latestArWeeklyMetric,
        arWeeklyActions: arWeeklyActionsData?.items || [],
        arWeeklyRevenueContributions: arWeeklyRevenueContributionData?.items || [],
    };
};

export default useARQueries;
